import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Select, Spin } from 'antd';
import { ErrorState, Props } from './types';
import styles from './style.module.css';
import { useOutgoingCallsUploadPopup } from '../../../hooks/features/useOutgoingCallsUploadPopup/useOutgoingCallsUploadPopup';
import FormPopup from '../FormPopup/FormPopup';
import { DownloadOutlined } from '@ant-design/icons';
import { UploadResult } from '../../../store/reducers/OutgoingCallsUploadSlice';
import { UploadResultBanner, UploadStatus } from './UploadResultBanner';
import { OutgoingCallsUploadScenarioFields } from './OutgoingCallsUploadScenarioFields';
import { validateOutgoingCallsUpload } from './validateOutgoingCallsUpload';

const NO_CONTRACT_MESSAGE =
  'Загрузка для выбранного сценария недоступна: нет контракта API';

const OutgoingCallsUploadPopup = memo(({ onCancel, open }: Props) => {
  const reportData = useOutgoingCallsUploadPopup();
  const [errors, setErrors] = useState<ErrorState>({});
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const isLoading = uploadStatus === 'loading';
  const hasResult =
    uploadStatus === 'success' ||
    uploadStatus === 'warning' ||
    uploadStatus === 'error';

  useEffect(() => {
    if (open) {
      reportData.handleReset();
      setErrors({});
      setUploadStatus('idle');
      setUploadResult(null);
    }
  }, [open]);

  const handleFocus = () => {
    setErrors({});
  };

  const handleMainButton = useCallback(async () => {
    const nextErrors = validateOutgoingCallsUpload(reportData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (reportData.hasNoContract) return;

    setUploadStatus('loading');
    setUploadResult(null);

    try {
      const result = await reportData.handleOk();
      setUploadResult(result);
      setUploadStatus(result.uploaded > 0 ? 'success' : 'warning');
    } catch {
      setUploadStatus('error');
    }
  }, [reportData]);

  const clearAndClose = useCallback(() => {
    reportData.handleReset();
    setErrors({});
    setUploadStatus('idle');
    setUploadResult(null);
    onCancel();
  }, [reportData, onCancel]);

  const handleRetry = useCallback(() => {
    reportData.handleFileReset();
    setErrors({});
    setUploadStatus('idle');
    setUploadResult(null);
  }, [reportData]);

  const handleDownloadTemplate = () => {
    if (!reportData.taskType) return;
    void reportData.handleDownloadTemplate();
  };

  const scenarioOptions = reportData.taskTypes.map((taskType) => ({
    value: taskType,
    label: reportData.taskTypeLabels[taskType] ?? taskType,
  }));

  const renderFooter = () => {
    if (hasResult) {
      return (
        <div key="buttonsRow" className={styles.buttonsRow}>
          <Button key="retry" onClick={handleRetry}>
            Загрузить другой файл
          </Button>
          <Button key="close" type="primary" onClick={clearAndClose}>
            Закрыть
          </Button>
        </div>
      );
    }

    return (
      <div key="buttonsRow" className={styles.buttonsRow}>
        {reportData.taskType && (
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadTemplate}
            loading={reportData.isTemplateLoading}
            disabled={
              isLoading ||
              reportData.isTemplateLoading ||
              reportData.hasNoContract
            }
          >
            <DownloadOutlined />
            &nbsp;Скачать шаблон
          </Button>
        )}
        <Button
          key="send"
          type="primary"
          onClick={handleMainButton}
          loading={isLoading}
          disabled={isLoading || reportData.hasNoContract}
        >
          Загрузить задания
        </Button>
      </div>
    );
  };

  return (
    <Modal
      className={styles.modalContent}
      open={open}
      onCancel={isLoading ? undefined : clearAndClose}
      closable={!isLoading}
      maskClosable={!isLoading}
      destroyOnHidden={true}
      footer={[renderFooter()]}
    >
      <p className={styles.titleModal}>Загрузка списка пациентов</p>

      <div className={styles.bodyWrapper}>
        {hasResult ? (
          <div className={styles.resultContainer}>
            <UploadResultBanner status={uploadStatus} result={uploadResult} />
          </div>
        ) : (
          <Spin
            spinning={isLoading || reportData.isTemplateLoading}
            tip={
              reportData.isTemplateLoading
                ? 'Скачивание шаблона...'
                : 'Загрузка файла...'
            }
          >
            <div
              className={
                isLoading || reportData.isTemplateLoading
                  ? styles.loadingOverlay
                  : undefined
              }
            >
              <Form layout="vertical" autoComplete="off">
                <Form.Item
                  label={
                    <span>
                      Сценарий <span className={styles.requiredMark}>*</span>
                    </span>
                  }
                  validateStatus={errors.taskType?.validateStatus}
                  help={errors.taskType?.help}
                >
                  <Select
                    value={reportData.taskType}
                    onChange={(value) => {
                      handleFocus();
                      reportData.onTaskTypeChange(value);
                    }}
                    options={scenarioOptions}
                    placeholder="Выберите сценарий"
                  />
                </Form.Item>

                <OutgoingCallsUploadScenarioFields
                  reportData={reportData}
                  errors={errors}
                  handleFocus={handleFocus}
                />

                {reportData.hasNoContract && (
                  <Alert
                    className={styles.noContractAlert}
                    type="warning"
                    showIcon={true}
                    message={NO_CONTRACT_MESSAGE}
                  />
                )}
              </Form>

              <FormPopup
                fileList={reportData.fileList}
                onFileChange={reportData.onFileChange}
                errors={errors}
                handleFocus={handleFocus}
              />
            </div>
          </Spin>
        )}
      </div>
    </Modal>
  );
});

OutgoingCallsUploadPopup.displayName = 'OutgoingCallsUploadPopup';
export default OutgoingCallsUploadPopup;
